import { createSelector } from '@reduxjs/toolkit';
import { StoreState, FilesIdData, FileStatus, DocumentTypes, FileMetadata } from 'types';

const getSourceFile = (state: StoreState) => state.files.source.file;
const getTargetFile = (state: StoreState) => state.files.target.file;

const getFilesList = (state: StoreState) => state.files.filesList;

const getIsFetchingInspectionFiles = (state: StoreState) => state.files.isFetchingInspectionFiles;

const getSourceFileStatus = (state: StoreState) => state.files.source.file.status;
const getTargetFileStatus = (state: StoreState) => state.files.target.file.status;

const getMultipleFileStatus = (state: StoreState) => state.files.multipleFileStatus;

const getOCRStatus = (state: StoreState): { source: FileStatus | ''; target: FileStatus | '' } => state.files.ocrStatus;

const getOCRHasRun = (state: StoreState) => state.files.ocrHasRun;

const getFileRequestKeys = (state: StoreState) => state.files.requestKeys;

const getShapesJobStatus = (state: StoreState) => state.files.shapesJobStatus;

const getFileErrorType = (state: StoreState) => state.files.fileErrorType;

const getUploadedFilesSelector = createSelector([getSourceFile, getTargetFile], (source, target) => ({
  source,
  target,
}));
const getMultiFileNamesJoined = (docType: DocumentTypes) => (state: StoreState) =>
  state.files[docType].file.mergedFileData?.reduce((prev: string, curr): string => {
    if (prev === '') {
      return curr.fileName;
    }
    return `${prev},${curr.fileName}`;
  }, '') || '';
const getFilesIds = createSelector(
  [getSourceFile, getTargetFile],
  (source, target): FilesIdData => ({
    masterFileId: source.fileId,
    sampleFileId: target.fileId,
  }),
);

const getConvertingFiles = createSelector([getSourceFileStatus, getTargetFileStatus], (source, target) => ({
  source: [FileStatus.queued, FileStatus.inprogress, FileStatus.pending].includes(source as FileStatus),
  target: [FileStatus.queued, FileStatus.inprogress, FileStatus.pending].includes(target as FileStatus),
}));

const getProcessingFlattenedText = createSelector([getOCRStatus], (ocrStatus) => ({
  source: [FileStatus.inprogress].includes(ocrStatus.source as FileStatus),
  target: [FileStatus.inprogress].includes(ocrStatus.target as FileStatus),
}));

const getIsOCR = createSelector([getSourceFile, getTargetFile], (source, target) => ({
  sourceIsOCR: source.isOCR,
  targetIsOCR: target.isOCR,
}));

const getFilesMetadata = createSelector(
  [getSourceFile, getTargetFile],
  (source, target): { masterMetadata: FileMetadata; sampleMetadata: FileMetadata } => ({
    masterMetadata: source.fileMetadata as FileMetadata,
    sampleMetadata: target.fileMetadata as FileMetadata,
  }),
);

const hasAutoSelectLayersSeparationsMetadata = createSelector(
  [getSourceFile, getTargetFile],
  (source, target): boolean => {
    const hasAutoSelectMetadataForFile = (fileMetadata: FileMetadata | undefined): boolean => {
      if (!fileMetadata) {
        return false;
      }
      // These fields are only present when auto-select layers/separations was enabled during file processing
      return (
        (fileMetadata.layersVisibility && fileMetadata.layersVisibility.length > 0) ||
        (fileMetadata.separationsToHide && fileMetadata.separationsToHide.length > 0) ||
        !!fileMetadata.selectedLayerName ||
        !!fileMetadata.selectedSeparationName ||
        (fileMetadata.autoSelectedShapes && fileMetadata.autoSelectedShapes.length > 0) ||
        !!fileMetadata.recommendedBoxType
      );
    };

    return hasAutoSelectMetadataForFile(source.fileMetadata) || hasAutoSelectMetadataForFile(target.fileMetadata);
  },
);

export const getActiveFileTransferId = (state: StoreState, documentType: DocumentTypes) =>
  state.files.activeFileTransferIds[documentType];

const getPendingReorderData = (state: StoreState) => state.files.pendingReorderData;

const getReadyFilesQueue = (state: StoreState) => state.files.readyFilesQueue;

const getPendingIdUpdates = (state: StoreState) => state.files.pendingIdUpdates;

const getFailedUploads = (state: StoreState) => state.files.failedUploads;

const getCancelledFileIds = (state: StoreState) => state.files.cancelledFileIds;

const getFileToBatchMap = (state: StoreState) => state.files.fileToBatchMap;

const getCancelledBatchKeys = (state: StoreState) => state.files.cancelledBatchKeys;

const getSourceFileUnavailable = (state: StoreState) => state.files.source.fileUnavailable;
const getTargetFileUnavailable = (state: StoreState) => state.files.target.fileUnavailable;

export {
  getUploadedFilesSelector,
  getFilesIds,
  getIsFetchingInspectionFiles,
  getConvertingFiles,
  getFilesList,
  getMultipleFileStatus,
  getOCRStatus,
  getOCRHasRun,
  getIsOCR,
  getMultiFileNamesJoined,
  getFileRequestKeys,
  getProcessingFlattenedText,
  getSourceFile,
  getTargetFile,
  getShapesJobStatus,
  getFilesMetadata,
  getFileErrorType,
  getPendingReorderData,
  getReadyFilesQueue,
  getPendingIdUpdates,
  getFailedUploads,
  getCancelledFileIds,
  getFileToBatchMap,
  getCancelledBatchKeys,
  hasAutoSelectLayersSeparationsMetadata,
  getSourceFileUnavailable,
  getTargetFileUnavailable,
};
